/** Shared formatting/derivation helpers for product data. */

/** Parse a value that may arrive as a number, numeric string, null or ''. */
export const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Discount percent as a number, 0 when absent. */
export const discountPercent = (product) => toNumber(product?.product_discount);

/** List price before discount. */
export const listPrice = (product) => toNumber(product?.variant?.price);

/** Price actually charged, after any flat product discount. */
export const finalPrice = (product) =>
  listPrice(product) * (1 - discountPercent(product) / 100);

/** `$12.34` — always two decimals, never `$NaN`. */
export const formatPrice = (value) => `$${toNumber(value).toFixed(2)}`;

/** Relative time such as "3 days ago"; safe against invalid dates. */
export const timeSince = (date) => {
  const then = new Date(date);
  if (Number.isNaN(then.getTime())) return '';

  const seconds = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  const plural = (n, unit) => `${n} ${unit}${n > 1 ? 's' : ''} ago`;

  if (weeks > 0) return plural(weeks, 'week');
  if (days > 0) return plural(days, 'day');
  if (hours > 0) return plural(hours, 'hour');
  if (minutes > 0) return plural(minutes, 'minute');
  return seconds <= 1 ? 'just now' : plural(seconds, 'second');
};

/** Build the page list for a paginator: [1, '…', 4, 5, 6, '…', 20] */
export const buildPageNumbers = (currentPage, lastPage) => {
  const current = toNumber(currentPage);
  const last = toNumber(lastPage);
  if (!current || last < 2) return [];

  const pages = [];
  const start = Math.max(1, current - 2);
  const end = Math.min(last, current + 2);

  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('…');
  }
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < last) {
    if (end < last - 1) pages.push('…');
    pages.push(last);
  }
  return pages;
};
